import { useEffect, useMemo, useState } from "react";
import { LuClock3 } from "react-icons/lu";

const SESSION_DURATION_MS = 30 * 60 * 1000;
const WIB_OFFSET_MS = 7 * 60 * 60 * 1000; // UTC+7

const pad2 = (value) => String(value).padStart(2, "0");

/**
 * Parse tanggal dari server dengan deteksi timezone otomatis.
 *
 * Backend tidak konsisten:
 *   - Kadang kirim UTC asli:  "2026-03-12T08:13:12Z" (benar, Z = UTC)
 *   - Kadang kirim WIB tapi pakai suffix Z: "2026-03-12T15:13:12Z" (salah, Z tapi isinya WIB)
 *
 * Cara deteksi: parse dulu sebagai UTC. Kalau hasilnya lebih dari 6 jam
 * di depan waktu lokal sekarang (tidak mungkin untuk waktu sesi aktif),
 * berarti server salah kirim WIB sebagai Z → koreksi dengan kurangi 7 jam.
 */
const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;

  const str = String(value);
  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) return null;

  const now = Date.now();
  const diffMs = parsed.getTime() - now;

  // Kalau parsed time lebih dari 6 jam di depan sekarang,
  // kemungkinan server kirim WIB tapi pakai Z → koreksi mundur 7 jam
  if (diffMs > 6 * 60 * 60 * 1000) {
    return new Date(parsed.getTime() - WIB_OFFSET_MS);
  }

  return parsed;
};

const formatHHMM = (date) => {
  return date.toLocaleTimeString("id-ID", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const resolveSessionWindow = (startAt, expiresAt) => {
  const start = toDate(startAt);
  const expires = toDate(expiresAt);

  if (!start && !expires) return null;
  if (start && expires) {
    if (expires.getTime() <= start.getTime()) return null;
    return { start, expires };
  }
  if (start && !expires) {
    return { start, expires: new Date(start.getTime() + SESSION_DURATION_MS) };
  }
  return {
    start: new Date(expires.getTime() - SESSION_DURATION_MS),
    expires,
  };
};

const SessionCountdownBanner = ({
  startAt,
  expiresAt,
  isSessionOver = false,
  isCustomer,
}) => {
  const sessionWindow = useMemo(
    () => resolveSessionWindow(startAt, expiresAt),
    [startAt, expiresAt],
  );

  const [remainingMs, setRemainingMs] = useState(() => {
    if (!sessionWindow) return 0;
    return Math.max(0, sessionWindow.expires.getTime() - Date.now());
  });

  useEffect(() => {
    if (!sessionWindow || isSessionOver) return undefined;

    setRemainingMs(Math.max(0, sessionWindow.expires.getTime() - Date.now()));

    const timer = setInterval(() => {
      const ms = Math.max(0, sessionWindow.expires.getTime() - Date.now());
      setRemainingMs(ms);
      if (ms === 0) clearInterval(timer);
    }, 1000);

    return () => clearInterval(timer);
  }, [sessionWindow, isSessionOver]);

  if (!sessionWindow || isSessionOver) return null;

  const remainingMinutes = Math.floor(remainingMs / 60000);
  const remainingSeconds = Math.floor((remainingMs % 60000) / 1000);

  return (
    <div
      className={`${isCustomer ? "px-2 py-0.5 rounded-md shadow-sm" : "px-3 py-2 rounded-lg shadow-md"} w-fit max-w-[90vw] whitespace-nowrap bg-rose-100 flex gap-4 items-center`}
    >
      <div
        className={`${isCustomer ? "gap-1" : "gap-2"} flex items-center text-error`}
      >
        <LuClock3 size={isCustomer ? 10 : 14} />
        <p className={`${isCustomer ? "text-sm" : "text-md"} font-medium`}>
          Durasi Sesi: {formatHHMM(sessionWindow.start)} -{" "}
          {formatHHMM(sessionWindow.expires)}
        </p>
      </div>

      <div className="flex items-center gap-1">
        <div
          className={`${isCustomer ? "text-xs min-w-4 h-4 rounded-sm" : "text-sm min-w-5 h-5 rounded-md"} bg-error text-white font-medium flex items-center justify-center`}
        >
          {pad2(remainingMinutes)}
        </div>
        <span className="text-error font-semibold text-base">:</span>
        <div
          className={`${isCustomer ? "text-xs min-w-4 h-4 rounded-sm" : "text-sm min-w-5 h-5 rounded-md"} bg-error text-white font-medium flex items-center justify-center`}
        >
          {pad2(remainingSeconds)}
        </div>
      </div>
    </div>
  );
};

export default SessionCountdownBanner;
